import React, { useState, useEffect, useRef } from "react";
import Vector from "../../assets/Vector.png";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { postData, getData } from "../../services/FetchNodeservices";

export default function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [openMenu, setOpenMenu] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("THEME") || "light");

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("THEME", theme);
  }, [theme]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted" && user?.id) {
          fetchNotifications();
        }
      });
    }
  }, [user]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };
  const [notifications, setNotifications] = useState([]);
  const [openNotifications, setOpenNotifications] = useState(false);

  const knownNotificationIds = useRef(new Set());

  const fetchNotifications = async () => {
    if (!user?.id) return;
    try {
      const response = await getData(`notifications/fetch/${user.id}`);
      if (response?.status && Array.isArray(response.data)) {
        const data = response.data;
        
        // Trigger system notifications for all unread entries that haven't been shown in this session
        data.forEach((n) => {
          if (!n.is_read && !knownNotificationIds.current.has(n.id)) {
            knownNotificationIds.current.add(n.id);
            
            if ("Notification" in window && Notification.permission === "granted") {
              try {
                const notif = new Notification("Morgan Placement", {
                  body: n.message,
                  icon: Vector
                });
                notif.onclick = () => {
                  window.focus();
                  handleMarkRead(n.id, n.link);
                };
              } catch (e) {
                console.error("Desktop notification error:", e);
              }
            }
          } else {
            knownNotificationIds.current.add(n.id);
          }
        });

        setNotifications(data);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
      const timer = setInterval(fetchNotifications, 30000);
      return () => clearInterval(timer);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      if (user?.id) {
        await postData("attendances/logout", { user_id: user.id });
      }

      dispatch({ type: "REMOVE_USER" });
      localStorage.removeItem("TOKEN");
      localStorage.removeItem("USER");

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleMarkRead = async (id, link) => {
    try {
      await postData(`notifications/read/${id}`);
      fetchNotifications();
      setOpenNotifications(false);
      if (link) {
        navigate(link);
      }
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    try {
      await postData(`notifications/read-all/${user.id}`);
      fetchNotifications();
    } catch (error) {
      console.error("Error marking all read:", error);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-slate-900/70 backdrop-blur-md border-b border-white/10 shadow-lg">
      <div className="max-w-full mx-auto px-4 py-3 flex items-center justify-between">

        {/* LEFT: LOGO */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/dashboard")}>
          <img
            src={Vector}
            alt="Logo"
            className="w-32 sm:w-36 object-contain drop-shadow-md transition hover:opacity-90"
          />
        </div>

        {/* RIGHT: USER MENU & NOTIFICATIONS */}
        <div className="flex items-center gap-4">
          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-200"
            title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {theme === "dark" ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728l.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          {user && (
            <div className="relative">
              {/* Bell Icon Button */}
              <button
                onClick={() => {
                  setOpenNotifications((prev) => !prev);
                  setOpenMenu(false);
                }}
                className="relative flex items-center justify-center w-10 h-10 
                           rounded-full bg-white/10 text-white hover:bg-white/20 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                  />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-slate-900">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {openNotifications && (
                <div
                  className="absolute right-0 mt-3 w-80 sm:w-96 bg-slate-900/90 backdrop-blur-xl border border-white/10 
                             rounded-xl shadow-xl text-white animate-fadeIn z-50 overflow-hidden"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
                    <span className="text-sm font-semibold">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-blue-400 hover:text-blue-300 transition"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  {/* Notification List */}
                  <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-slate-400">
                        No notifications yet
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          onClick={() => handleMarkRead(n.id, n.link)}
                          className={`px-4 py-3 text-sm text-left cursor-pointer hover:bg-white/5 transition flex flex-col gap-1 ${
                            !n.is_read ? "bg-white/10" : ""
                          }`}
                        >
                          <p className="text-slate-100 font-medium leading-snug">{n.message}</p>
                          <span className="text-[10px] text-slate-400">
                            {new Date(n.created_at).toLocaleString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {!user ? (
            <button
              onClick={() => navigate("/login")}
              className="bg-yellow-400 text-black px-5 py-2 rounded-full font-medium 
                         hover:bg-yellow-300 transition shadow-md"
            >
              Login
            </button>
          ) : (
            <div className="relative">
              {/* Avatar Button */}
              <button
                onClick={() => {
                  setOpenMenu((prev) => !prev);
                  setOpenNotifications(false);
                }}
                className="flex items-center justify-center w-10 h-10 
                           rounded-full bg-white/80 text-slate-900 font-semibold
                           shadow hover:bg-white transition"
              >
                {user?.first_name?.[0] || "U"}
              </button>

              {/* Dropdown Menu */}
              {openMenu && (
                <div
                  className="absolute right-0 mt-3 w-60 bg-slate-900/90 backdrop-blur-xl border border-white/10 
                             rounded-xl shadow-xl p-3 text-white animate-fadeIn z-50"
                >
                  <div className="px-2 py-2 border-b border-white/10">
                    <p className="text-sm font-semibold">{user.first_name} {user.last_name}</p>
                    <p className="text-xs text-slate-300">{user.email}</p>
                    <p className="text-xs text-slate-300">{user.mobile_num}</p>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 mt-2 
                               hover:bg-white/10 rounded-lg text-sm transition"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/* Optional Animation */
<style>
{`
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeIn 0.15s ease-out; }
`}
</style>
